// Check-in (/). The approved Sept 13 2026 design and copy, ported from
// ottos-consult src/ConsultPage.jsx (StartConsult), plus the v2 pieces (design
// 3.2 row 1, 3.4, 7.7, 7.8, 9.2, Wes's Sept 28 decisions):
//   - "Talk with Otto on video" or "Just talk, visual off"
//   - industry list plus "Other" with free text
//   - English or Espanol, shown only when the server offers Spanish
//   - a required consent box whose text comes from config
//   - Turnstile (site key from config), UTM, ?ch= and ?src= capture
//   - counts-only funnel steps, an in-app browser warning
//   - "Prefer not to talk? A person will reach out"
//   - the 3D model download starts at the first touch of the form, not on load
//
// The Start tap primes both audio contexts and asks for the mic BEFORE any
// await (iOS only allows audio inside a gesture), then checks in and moves to
// the call, which starts on its own.
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { CheckinRequest, type CheckinResponse } from "@otto/api";
import type { z } from "zod";
import {
  getCheckinConfig,
  getResumable,
  postCheckin,
  postHumanRequest,
  sendFunnel,
  type ApiFailure,
  type CheckinConfig,
} from "./api";
import { preloadAvatar } from "./avatar/loadAvatar";
import { primeCallAudio, releasePrimedCallAudio } from "./call/audio/audioPrime";
import { releaseMicrophone, requestMicrophone } from "./call/audio/micPermission";
import { InAppBrowserNotice } from "./checkin/InAppBrowserNotice";
import { MicCheck, micProblemText } from "./checkin/MicCheck";
import { Turnstile, type TurnstileHandle } from "./checkin/Turnstile";
import { deviceClass, inAppBrowser, readDeviceHints, saveDataOn, usesPhoneModel } from "./device";
import { browserLanguages, defaultLanguage, type Language } from "./language";
import { formatPhoneInput, normalizePhone } from "./phone";
import type { FunnelStep, HumanRequest } from "@otto/api";
import { firstNameOf, saveConsultRecord } from "./session";
import { DEV_FALLBACK_CONSENT, SITE, stringsFor, type ConsultStrings } from "./strings";
import { stripDashes } from "./visibleText";
import "./consult.css";

type Mode = "video" | "voice";
type Industry = z.infer<typeof CheckinRequest>["industry"];

interface FormState {
  name: string;
  business: string;
  industry: string;
  email: string;
  phone: string;
  mode: Mode;
  consent: boolean;
  contactMethod: HumanRequest["contact_method"];
}

type FieldErrors = Partial<
  Record<"name" | "business" | "industry" | "industryOther" | "email" | "phone" | "consent", boolean>
>;
const FIELD_ERROR_KEYS = {
  name: 1,
  business: 1,
  industry: 1,
  industryOther: 1,
  email: 1,
  phone: 1,
  consent: 1,
} as const;

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

/** ?ch=, ?src= and utm_* from the link, plus the referrer (design 3.4 step 2). */
export function readAttribution(search: string, referrer: string) {
  const params = new URLSearchParams(search);
  const utm: Partial<Record<(typeof UTM_KEYS)[number], string>> = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) utm[key] = value.slice(0, 200);
  }
  const ch = params.get("ch")?.toLowerCase() ?? "";
  const src = params.get("src") ?? "";
  return {
    channel: /^[a-z][a-z0-9_]{0,63}$/.test(ch) ? ch : undefined,
    source_code: /^[a-z0-9_-]{1,64}$/i.test(src) ? src : undefined,
    utm: Object.keys(utm).length ? utm : undefined,
    referrer: referrer ? referrer.slice(0, 2000) : undefined,
  };
}

/** One free-text industry box (Wes, Sept 29 2026): a typed answer that matches a
 *  list choice is sent as that choice; anything else goes as Other with the words typed. */
export function industryFields(text: string, choices: readonly string[]): { industry: Industry; industry_other?: string } {
  const typed = text.trim();
  const hit = choices.find((c) => c !== "Other" && c.toLowerCase() === typed.toLowerCase());
  return hit ? { industry: hit as Industry } : { industry: "Other" as Industry, industry_other: typed };
}

/** Friendly checks first; the contract schema (CheckinRequest) is the final gate. */
export function validateForm(form: FormState, needsConsent: boolean): FieldErrors {
  const errors: FieldErrors = {};
  if (!form.name.trim()) errors.name = true;
  if (!form.business.trim()) errors.business = true;
  if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) errors.email = true;
  if (!normalizePhone(form.phone)) errors.phone = true;
  if (!form.industry.trim()) errors.industry = true;
  if (needsConsent && !form.consent) errors.consent = true;
  return errors;
}

function firstErrorText(s: ConsultStrings, errors: FieldErrors): string {
  if (errors.name || errors.business) return s.errors.required;
  if (errors.email) return s.errors.email;
  if (errors.phone) return s.errors.phone;
  if (errors.industry) return s.errors.required;
  if (errors.industryOther) return s.errors.industryOther;
  if (errors.consent) return s.errors.consent;
  return s.errors.generic;
}

export function checkinFailureText(s: ConsultStrings, failure: ApiFailure): string {
  switch (failure.code) {
    case "network":
      return s.errors.network;
    case "rate_limited":
      return s.errors.rateLimited;
    case "turnstile_failed":
      return s.errors.botCheck;
    case "paused":
      return s.errors.paused;
    case "between_calls":
      return s.errors.betweenCalls;
    case "booked_up":
      return s.errors.bookedUp;
    case "at_capacity":
      return s.errors.atCapacity;
    case "mode_unavailable":
      return s.errors.modeUnavailable;
    case "language_unavailable":
      return s.errors.languageUnavailable;
    default:
      return s.errors.generic;
  }
}

type ConfigState =
  { kind: "loading" } | { kind: "ready"; config: CheckinConfig } | { kind: "error"; failure: ApiFailure };

/** DEV ONLY (?review=checkin): a sample config so the form can be reviewed without otto-app. Stripped from production builds. */
function devSampleConfig(): { ok: true; data: CheckinConfig } {
  return {
    ok: true,
    data: {
      ok: true,
      open: true,
      closed_reason: null,
      legacy_url: null,
      turnstile_site_key: "",
      modes: { video: true, voice: true },
      languages: ["en", "es"],
      industries: [
        "Small Business",
        "Medical",
        "Legal",
        "Home Services",
        "Nonprofit",
        "Professional Services",
        "Real Estate",
        "Construction",
        "Other",
      ],
      consent_notice_version: "dev-review",
      consent_notice: null,
      privacy_url: null,
      callback_url: null,
      agenda: [],
      target_minutes: 15,
      cap_minutes: 22,
      client_audio: null,
    },
  };
}

/**
 * One check-in field in the approved look. The label names the control; the
 * hint is linked with aria-describedby, so a screen reader reads "EMAIL" and
 * then the hint, instead of one long name (design 7.7).
 */
function Field({
  id,
  label,
  hint,
  wide = false,
  invalid = false,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  wide?: boolean;
  invalid?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`oo-design-field${wide ? " oo-design-field--wide" : ""}${invalid ? " is-invalid" : ""}`}>
      <label className="oo-design-field__label" htmlFor={id}>
        {label}
      </label>
      {children}
      {hint && <small id={`${id}-hint`}>{hint}</small>}
    </div>
  );
}

export function CheckInPage() {
  const navigate = useNavigate();
  const hints = useMemo(() => readDeviceHints(), []);
  const device = useMemo(() => deviceClass(hints), [hints]);
  const inApp = useMemo(() => inAppBrowser(hints.userAgent), [hints]);
  const attribution = useMemo(
    () =>
      readAttribution(
        typeof window !== "undefined" ? window.location.search : "",
        typeof document !== "undefined" ? document.referrer : "",
      ),
    [],
  );
  const [configState, setConfigState] = useState<ConfigState>({ kind: "loading" });
  // CR-016: this browser's last consult dropped and can be picked back up (the server checks the cookie).
  const [resumeName, setResumeName] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void getResumable().then((r) => {
      if (!cancelled && r.ok && r.data.resumable) setResumeName(r.data.first_name);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const [language, setLanguage] = useState<Language>("en");
  const [form, setForm] = useState<FormState>({
    name: "",
    business: "",
    industry: "",
    email: "",
    phone: "",
    mode: "video",
    consent: false,
    contactMethod: "email",
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string>("");
  const [token, setToken] = useState<string | null>(null);
  const [botCheckDown, setBotCheckDown] = useState(false);
  const [starting, setStarting] = useState(false);
  const [touched, setTouched] = useState(false);
  const [inAppDismissed, setInAppDismissed] = useState(false);
  const [personMode, setPersonMode] = useState(false);
  const [personState, setPersonState] = useState<"idle" | "sending" | "done" | "unavailable">("idle");
  const turnstileRef = useRef<TurnstileHandle>(null);
  const s = stringsFor(language);

  const funnel = useCallback(
    (step: FunnelStep, extra: { mode?: Mode } = {}) => {
      sendFunnel({
        step,
        device_class: device,
        in_app_browser: Boolean(inApp),
        language,
        ...(attribution.channel ? { channel: attribution.channel } : {}),
        ...extra,
      });
    },
    [attribution.channel, device, inApp, language],
  );

  // Page view, and the config the page needs before anyone types.
  useEffect(() => {
    funnel("page_view");
    if (inApp) funnel("in_app_browser_seen");
    let cancelled = false;
    const source =
      import.meta.env.DEV && new URLSearchParams(window.location.search).get("review") === "checkin"
        ? Promise.resolve(devSampleConfig()) // DEV ONLY: review the form without otto-app running
        : getCheckinConfig();
    void source.then((result) => {
      if (cancelled) return;
      if (!result.ok) {
        setConfigState({ kind: "error", failure: result });
        return;
      }
      const config = result.data;
      if (config.closed_reason === "legacy" && config.legacy_url) {
        // ROUTE_TO_LEGACY: the rollback switch sends new check-ins to the old consult.
        window.location.replace(config.legacy_url);
        return;
      }
      setLanguage(defaultLanguage(config.languages, browserLanguages()));
      setForm((current) => ({ ...current, mode: config.modes.video ? "video" : "voice" }));
      setConfigState({ kind: "ready", config });
    });
    return () => {
      cancelled = true;
    };
    // Once per page load: the funnel callback changes with the language, the page view must not repeat.
  }, []);

  const config = configState.kind === "ready" ? configState.config : null;

  // Consent text comes from config. Only a local dev build may fall back to the v0 draft.
  const consent = useMemo(() => {
    if (!config) return null;
    const notice = config.consent_notice;
    const text = notice
      ? (notice.text[language] ?? notice.text.en)
      : import.meta.env.DEV
        ? DEV_FALLBACK_CONSENT[language]
        : null;
    if (!text) return null;
    const version = notice?.version ?? config.consent_notice_version;
    if (!version) return null;
    return { text: stripDashes(text), version };
  }, [config, language]);

  useEffect(() => {
    if (config && !consent)
      console.error("[otto] check-in closed: otto-app sent no consent notice (the consent_notice setting is empty)");
  }, [config, consent]);

  // First touch of the form starts the 3D download, only for video and not with data saver on (design 3.4 step 1).
  useEffect(() => {
    if (!touched || form.mode !== "video" || saveDataOn()) return;
    preloadAvatar(usesPhoneModel(hints) ? "phone" : "desktop");
  }, [touched, form.mode, hints]);

  const markTouched = (event?: SyntheticEvent) => {
    // A touch on the mode choice is handled by chooseMode, after the choice is
    // known: a pointerdown on "Just talk, visual off" must not start the 3D download.
    const target = event?.target as HTMLElement | undefined;
    if (target?.closest?.(".oo-mode-choice")) return;
    if (touched) return;
    setTouched(true);
    funnel("form_started");
  };

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    if (key in FIELD_ERROR_KEYS) setFieldErrors((current) => ({ ...current, [key as keyof FieldErrors]: false }));
  };

  const chooseMode = (mode: Mode) => {
    update("mode", mode);
    funnel("mode_chosen", { mode });
    if (!touched) {
      setTouched(true);
      funnel("form_started");
    }
  };

  const buildRequest = (): z.input<typeof CheckinRequest> | null => {
    const botToken = token ?? (config && !config.turnstile_site_key ? "no-widget" : null);
    if (!config || !consent || !botToken) return null;
    return {
      turnstile_token: botToken,
      full_name: form.name.trim(),
      business_name: form.business.trim(),
      email: form.email.trim(),
      phone: normalizePhone(form.phone),
      ...industryFields(form.industry, config.industries ?? []),
      mode: form.mode,
      language,
      consent: { agreed: true, notice_version: consent.version },
      ...(attribution.channel ? { channel: attribution.channel } : {}),
      ...(attribution.source_code ? { source_code: attribution.source_code } : {}),
      ...(attribution.utm ? { utm: attribution.utm } : {}),
      ...(attribution.referrer ? { referrer: attribution.referrer } : {}),
      landing_url: typeof window !== "undefined" ? `${window.location.origin}${window.location.pathname}` : undefined,
    };
  };

  const showError = (errors: FieldErrors, text: string) => {
    setFieldErrors(errors);
    setFormError(text);
  };

  const submitConsult = async () => {
    const errors = validateForm(form, true);
    if (Object.values(errors).some(Boolean)) return showError(errors, firstErrorText(s, errors));
    if (!token && config?.turnstile_site_key) return showError({}, botCheckDown ? s.errors.botCheckUnavailable : s.errors.botCheck);
    const request = buildRequest();
    const parsed = request ? CheckinRequest.safeParse(request) : null;
    if (!parsed?.success) return showError({}, s.errors.generic);

    setFormError("");
    setStarting(true);
    funnel("start_tapped", { mode: form.mode });
    // Inside the tap, before any await: unlock audio for iOS, then the mic.
    primeCallAudio();
    const mic = await requestMicrophone();
    if (!mic.granted) {
      releasePrimedCallAudio();
      setStarting(false);
      funnel("mic_denied");
      return showError({}, micProblemText(s, mic.problem));
    }
    funnel("mic_granted");

    const result = await postCheckin(parsed.data);
    if (!result.ok) {
      releasePrimedCallAudio();
      releaseMicrophone();
      setStarting(false);
      if (result.code === "legacy" && result.redirectUrl) {
        window.location.replace(result.redirectUrl);
        return;
      }
      // A Turnstile token is single use: get a fresh one for the next try.
      turnstileRef.current?.reset();
      return showError({}, checkinFailureText(s, result));
    }
    funnel("checked_in", { mode: result.data.mode });
    rememberAndGo(result.data);
  };

  const rememberAndGo = (checkin: z.infer<typeof CheckinResponse>) => {
    saveConsultRecord({
      consult_id: checkin.consult_id,
      live_path: checkin.live_path,
      live_token: checkin.live_token,
      live_token_expires_at: checkin.live_token_expires_at,
      mode: checkin.mode,
      language: checkin.language,
      first_name: firstNameOf(form.name).slice(0, 100),
      business_name: form.business.trim().slice(0, 200),
      agenda: checkin.agenda,
      target_minutes: checkin.target_minutes,
      cap_minutes: checkin.cap_minutes,
      live_origin: checkin.live_origin,
      client_audio: checkin.client_audio,
    });
    // The token also travels in memory, so the call still works when storage is blocked.
    navigate(`/call/${checkin.consult_id}`, {
      state: {
        fromCheckin: true,
        checkin,
        firstName: firstNameOf(form.name),
      },
    });
  };

  const submitPerson = async () => {
    const errors = validateForm(form, false);
    if (Object.values(errors).some(Boolean)) return showError(errors, firstErrorText(s, errors));
    if (!token && config?.turnstile_site_key) return showError({}, botCheckDown ? s.errors.botCheckUnavailable : s.errors.botCheck);
    setFormError("");
    setPersonState("sending");
    const result = await postHumanRequest({
      turnstile_token: token ?? "no-widget",
      full_name: form.name.trim(),
      business_name: form.business.trim(),
      email: form.email.trim(),
      phone: normalizePhone(form.phone),
      ...industryFields(form.industry, config?.industries ?? []),
      language,
      contact_method: form.contactMethod,
      ...(attribution.channel ? { channel: attribution.channel } : {}),
      ...(attribution.source_code ? { source_code: attribution.source_code } : {}),
    });
    turnstileRef.current?.reset();
    // Honest: if it could not be sent, say so and give the direct link.
    setPersonState(result.ok ? "done" : "unavailable");
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (starting || personState === "sending") return;
    void (personMode ? submitPerson() : submitConsult());
  };

  // The "Prefer not to talk?" link was removed from the page (Wes, Sept 29 2026);
  // the person route stays in code, unreachable, in case it comes back.
  void setPersonMode;

  const callbackUrl = config?.callback_url ?? SITE.contact;
  const closed = configState.kind === "error" || (config && (!config.open || !consent));
  const closedText =
    configState.kind === "error"
      ? s.errors.network
      : config && !config.open
        ? config.closed_reason === "between_calls"
          ? s.errors.betweenCalls
          : config.closed_reason === "booked_up"
            ? s.errors.bookedUp
            : s.errors.paused
        : s.errors.notOpen;
  const showLanguage = Boolean(config && config.languages.length > 1);
  const modes = config?.modes ?? { video: true, voice: true };
  const privacyUrl = config?.privacy_url ?? SITE.privacy;
  const showInApp = Boolean(inApp) && !inAppDismissed;

  return (
    <div className="consult-root oo-consult-root" lang={language}>
      <div className="oo-consult-content">
        <main className="oo-intake-layout">
          <section className="oo-intake-media" aria-label={s.checkin.mediaAlt}>
            <img src="/images/consult/otto-consult-call.jpg" alt={s.checkin.mediaAlt} />
            <div className="oo-intake-media__status">
              <span />
              <p>{s.checkin.mediaStatus}</p>
            </div>
          </section>

          <div className="oo-intake-form-panel">
            <div className="oo-intake-copy">
              <h1>
                {s.checkin.titleLead} <span>{s.checkin.titleTail}</span>
              </h1>
              <p>{form.mode === "voice" ? s.checkin.introVoice : s.checkin.introVideo}</p>
            </div>

            <div className="oo-intake-steps" aria-label={s.checkin.stepsLabel}>
              <span className="is-active">
                <b>1</b>
                <p>{s.checkin.step1}</p>
                <small>{s.checkin.step1Time}</small>
              </span>
              <i />
              <span>
                <b>2</b>
                <p>{s.checkin.step2}</p>
                <small>{s.checkin.step2Time}</small>
              </span>
              <i />
              <span>
                <b>3</b>
                <p>{s.checkin.step3}</p>
                <small>{s.checkin.step3Time}</small>
              </span>
            </div>

            {showInApp && (
              <InAppBrowserNotice s={s} userAgent={hints.userAgent} onDismiss={() => setInAppDismissed(true)} />
            )}

            {resumeName && (
              <section className="oo-design-form oo-resume-offer" role="status">
                <div className="oo-design-form__section">
                  <h2>{s.checkin.resumeOffer(resumeName)}</h2>
                  <p>{s.checkin.resumeOfferDetail}</p>
                  <a className="oo-thank-you__button" href="/resume">
                    {s.checkin.resumeOfferButton}
                  </a>
                </div>
              </section>
            )}

            {personState === "done" ? (
              <section className="oo-design-form oo-person-done" role="status">
                <div className="oo-design-form__section">
                  <h2>{s.person.done}</h2>
                  <p>{s.person.doneDetail}</p>
                </div>
              </section>
            ) : (
              <form
                className="oo-design-form"
                onSubmit={onSubmit}
                noValidate
                onFocusCapture={markTouched}
                onPointerDownCapture={markTouched}
              >
                <div className="oo-design-form__bar">
                  <span>
                    <img src="/images/consult/starburst-yellow.png" alt="" />
                    {personMode ? s.person.title.toUpperCase() : s.checkin.formBar}
                  </span>
                  <p>{s.checkin.formBarNote}</p>
                </div>

                {closed ? (
                  <div className="oo-design-form__section">
                    <p className="oo-design-form__closed" role="status">
                      {configState.kind === "loading" ? "" : closedText}
                    </p>
                    <p>
                      <a href={callbackUrl}>{s.person.title}</a>
                    </p>
                  </div>
                ) : (
                  <>
                    {!personMode && (
                      <div className="oo-design-form__section">
                        <p className="oo-design-form__eyebrow" id="oo-mode-label">
                          {s.checkin.howToTalk}
                        </p>
                        <div className="oo-mode-choice" role="radiogroup" aria-labelledby="oo-mode-label">
                          {(["video", "voice"] as const).map((mode) => (
                            <label
                              key={mode}
                              className={`oo-mode-option${form.mode === mode ? " is-selected" : ""}${modes[mode] ? "" : " is-disabled"}`}
                            >
                              <input
                                type="radio"
                                name="mode"
                                value={mode}
                                checked={form.mode === mode}
                                disabled={!modes[mode]}
                                onChange={() => chooseMode(mode)}
                              />
                              <b>{mode === "video" ? s.checkin.modeVideo : s.checkin.modeVoice}</b>
                              <small>{mode === "video" ? s.checkin.modeVideoNote : s.checkin.modeVoiceNote}</small>
                            </label>
                          ))}
                        </div>
                        {showLanguage && (
                          <fieldset className="oo-language-choice">
                            <legend className="oo-design-form__eyebrow">{s.checkin.language}</legend>
                            {config?.languages.map((code) => (
                              <label key={code} className={language === code ? "is-selected" : ""}>
                                <input
                                  type="radio"
                                  name="language"
                                  value={code}
                                  checked={language === code}
                                  onChange={() => setLanguage(code)}
                                />
                                {code === "en" ? "English" : "Español"}
                              </label>
                            ))}
                          </fieldset>
                        )}
                      </div>
                    )}

                    <div className="oo-design-form__section">
                      <p className="oo-design-form__eyebrow">{s.checkin.aboutYou}</p>
                      <div className="oo-design-form__grid">
                        <Field id="oo-name" label={s.checkin.name} invalid={fieldErrors.name}>
                          <input
                            id="oo-name"
                            value={form.name}
                            onChange={(e) => update("name", e.target.value)}
                            autoComplete="name"
                            aria-invalid={fieldErrors.name || undefined}
                          />
                        </Field>
                        <Field id="oo-business" label={s.checkin.company} invalid={fieldErrors.business}>
                          <input
                            id="oo-business"
                            value={form.business}
                            onChange={(e) => update("business", e.target.value)}
                            autoComplete="organization"
                            aria-invalid={fieldErrors.business || undefined}
                          />
                        </Field>
                        <Field id="oo-industry" label={s.checkin.industry} invalid={fieldErrors.industry}>
                          <input
                            id="oo-industry"
                            list="oo-industry-list"
                            value={form.industry}
                            onChange={(e) => update("industry", e.target.value)}
                            placeholder={s.checkin.industryPlaceholder}
                            maxLength={200}
                            autoComplete="off"
                            aria-invalid={fieldErrors.industry || undefined}
                          />
                          <datalist id="oo-industry-list">
                            {(config?.industries ?? []).filter((i) => i !== "Other").map((industry) => (
                              <option key={industry} value={industry} />
                            ))}
                          </datalist>
                        </Field>
                        <Field
                          id="oo-email"
                          label={s.checkin.email}
                          hint={s.checkin.emailHint}
                          invalid={fieldErrors.email}
                        >
                          <input
                            id="oo-email"
                            type="email"
                            value={form.email}
                            onChange={(e) => update("email", e.target.value)}
                            autoComplete="email"
                            aria-describedby="oo-email-hint"
                            aria-invalid={fieldErrors.email || undefined}
                          />
                        </Field>
                        <Field
                          id="oo-phone"
                          label={s.checkin.phone}
                          hint={s.checkin.phoneHint}
                          wide
                          invalid={fieldErrors.phone}
                        >
                          <input
                            id="oo-phone"
                            type="tel"
                            inputMode="tel"
                            autoComplete="tel"
                            value={form.phone}
                            onChange={(e) => update("phone", formatPhoneInput(e.target.value))}
                            aria-describedby="oo-phone-hint"
                            aria-invalid={fieldErrors.phone || undefined}
                          />
                        </Field>
                      </div>
                    </div>

                    {personMode ? (
                      <div className="oo-design-form__section oo-design-form__section--quick">
                        <fieldset className="oo-contact-choice">
                          <legend className="oo-design-form__eyebrow">{s.person.how}</legend>
                          {(["email", "text", "call"] as const).map((method) => (
                            <label key={method} className={form.contactMethod === method ? "is-selected" : ""}>
                              <input
                                type="radio"
                                name="contact"
                                value={method}
                                checked={form.contactMethod === method}
                                onChange={() => update("contactMethod", method)}
                              />
                              {s.person[method]}
                            </label>
                          ))}
                        </fieldset>
                        <p className="oo-quick-copy">{s.person.intro}</p>
                      </div>
                    ) : (
                      <div className="oo-design-form__section oo-design-form__section--quick">
                        <p className="oo-design-form__eyebrow">{s.checkin.beforeYouStart}</p>
                        <div className="oo-quick-grid">
                          <MicCheck s={s} onResult={(granted) => funnel(granted ? "mic_granted" : "mic_denied")} />
                          <div>
                            <p className="oo-quick-label oo-quick-label--camera">
                              <svg aria-hidden="true" width="34" height="26" viewBox="0 0 34 26" fill="none">
                                <rect
                                  x="2"
                                  y="6"
                                  width="20"
                                  height="15"
                                  rx="3"
                                  stroke="currentColor"
                                  strokeWidth="2.4"
                                />
                                <path
                                  d="M22.5 11.5 L31 6.5 V19.5 L22.5 14.5 Z"
                                  stroke="currentColor"
                                  strokeWidth="2.4"
                                  strokeLinejoin="round"
                                />
                                <path d="M3.5 24 L25 2.5" stroke="#C4553F" strokeWidth="2.6" strokeLinecap="round" />
                              </svg>
                              {s.checkin.cameraLabel}
                            </p>
                            <p className="oo-quick-copy">
                              {form.mode === "voice" ? s.checkin.cameraCopyVoice : s.checkin.cameraCopyVideo}
                            </p>
                          </div>
                        </div>
                        {consent && (
                          <label className={`oo-consent${fieldErrors.consent ? " is-invalid" : ""}`}>
                            <input
                              type="checkbox"
                              checked={form.consent}
                              onChange={(e) => update("consent", e.target.checked)}
                              required
                              aria-invalid={fieldErrors.consent || undefined}
                              aria-describedby="oo-consent-text"
                            />
                            <span>
                              <b>{s.checkin.consentLabel}</b> <span id="oo-consent-text">{consent.text}</span>{" "}
                              <a href={privacyUrl} target="_blank" rel="noreferrer">
                                {s.checkin.privacyLink}
                              </a>
                            </span>
                          </label>
                        )}
                      </div>
                    )}

                    <div className="oo-design-form__section oo-design-form__section--check">
                      {config?.turnstile_site_key ? (
                        <Turnstile
                          ref={turnstileRef}
                          siteKey={config.turnstile_site_key}
                          language={language}
                          label={s.checkin.botCheckLabel}
                          onToken={setToken}
                          onUnavailable={() => setBotCheckDown(true)}
                        />
                      ) : (
                        null
                      )}
                      {formError && (
                        <div className="oo-design-form__error" role="alert">
                          {formError}
                        </div>
                      )}
                      {personState === "unavailable" && (
                        <div className="oo-design-form__error" role="alert">
                          {s.person.unavailable} <a href={callbackUrl}>{callbackUrl.replace(/^https?:\/\//, "")}</a>
                        </div>
                      )}
                    </div>

                    <div className="oo-design-form__submit">
                      {personMode ? (
                        <>
                          <button type="submit" disabled={personState === "sending"}>
                            {personState === "sending" ? s.person.sending : s.person.send}
                          </button>
                          <button type="button" className="oo-link-button" onClick={() => setPersonMode(false)}>
                            {s.person.back}
                          </button>
                        </>
                      ) : (
                        <>
                          <button type="submit" disabled={starting}>
                            {starting ? s.checkin.starting : s.checkin.start}
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </form>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
